import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import { getPortfolioItems, getOptimizedImageUrl } from '../sanity/client';
import { useCachedTexts } from '../hooks/useCachedTexts';
import './PortfolioPage.css';

const PortfolioPage = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { getText } = useCachedTexts('portfolio');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchPortfolio = async () => {
      try {
        const data = await getPortfolioItems();
        
        // Transform Sanity data to portfolio items
        const portfolioItems = data.map(item => {
          // Collect all images (mainImage + secondImage if exists)
          const images = [];
          if (item.mainImage?.asset?.url) {
            images.push(getOptimizedImageUrl(item.mainImage, { width: 600, quality: 85 }));
          }
          if (item.secondImage?.asset?.url) {
            images.push(getOptimizedImageUrl(item.secondImage, { width: 600, quality: 85 }));
          }
          
          // Ensure URL has protocol
          let formattedUrl = item.externalUrl;
          if (formattedUrl && !formattedUrl.match(/^https?:\/\//i)) {
            formattedUrl = 'https://' + formattedUrl;
          }
          
          return {
            id: item._id,
            img: item.mainImage ? getOptimizedImageUrl(item.mainImage, { width: 600, quality: 85 }) : '',
            images: images, // Pass all images
            url: formattedUrl,
            title: item.title,
            externalUrl: formattedUrl,
            isExternal: !!item.externalUrl
          };
        });
        
        setItems(portfolioItems);
      } catch (error) {
        console.error('Error fetching portfolio:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPortfolio();
  }, []);

  return (
    <div className="portfolio-page">
      <SEO 
        title={getText('title', 'Portfolio - Projects and Clients')}
        description="Discover the communications and PR projects delivered by Magicomms for companies, athletes, sports clubs and investment funds. Sports, corporate and digital content communications."
        keywords="communications portfolio, PR projects, sports communications, magicomms clients, communications case studies"
        lang="it"
      />

      <div className="portfolio-container">
        <h1>{getText('title', t('portfolio.title'))}</h1>
        <p className="portfolio-subtitle">{getText('subtitle', t('portfolio.subtitle'))}</p>
        
        <section className="portfolio-gallery-section">
          {loading ? (
            <div className="masonry-skeleton">
              {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                <div key={i} className="masonry-skeleton-item" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="portfolio-empty">
              <p>{t('portfolio.empty')}</p>
            </div>
          ) : (
            <div className="portfolio-grid">
              {items.map((item) => {
                const hasMultipleImages = item.images && item.images.length > 1;
                
                const cardContent = (
                  <>
                    <div className={`portfolio-card-image ${hasMultipleImages ? 'has-multiple' : ''}`}>
                      {hasMultipleImages ? (
                        <div className="portfolio-card-images-grid">
                          {item.images.slice(0, 2).map((imgUrl, idx) => (
                            <img key={idx} src={imgUrl} alt={`${item.title} - Image ${idx + 1}`} />
                          ))}
                        </div>
                      ) : (
                        <img src={item.img} alt={item.title} />
                      )}
                    </div>
                    <div className="portfolio-card-content">
                      <h3>{item.title}</h3>
                    </div>
                  </>
                );

                // Only external URLs are clickable from portfolio page
                if (item.isExternal) {
                  // External URL - use <a> tag
                  return (
                    <a
                      key={item.id}
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="portfolio-card portfolio-card-clickable portfolio-card-external"
                    >
                      {cardContent}
                    </a>
                  );
                } else {
                  // No external URL - static card (even if hasContent is true)
                  return (
                    <div
                      key={item.id}
                      className="portfolio-card portfolio-card-static"
                    >
                      {cardContent}
                    </div>
                  );
                }
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default PortfolioPage;
